import { TitleCasePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import { BookingApiService, starpayError } from '../data/booking-api.service';
import { readLastBooking } from '../data/last-booking';
import { BookingStateService } from '../data/booking-state.service';
import { Booking, PAYMENT_METHODS } from '../models/booking.models';
import { formatCurrency, formatDateLong, formatTime12 } from '../util/format';

interface ConfirmedTicket {
  booking: Booking;
  label: string;
  routeLabel: string;
  dateLabel: string;
  departureTime: string;
  terminal: string;
  seatLabels: string;
  paymentLabel: string;
  qrDataUrl: string;
}

@Component({
  selector: 'app-booking-confirmed-page',
  standalone: true,
  imports: [AppIconComponent, RouterLink, TitleCasePipe],
  templateUrl: './booking-confirmed.page.html',
  styleUrl: './booking-confirmed.page.scss',
})
export class BookingConfirmedPage implements OnInit {
  private readonly bookingState = inject(BookingStateService);
  private readonly bookingApi = inject(BookingApiService);
  private readonly route = inject(ActivatedRoute);

  readonly tickets = signal<ConfirmedTicket[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly paymentProblem = signal('');
  readonly resumeUrl = signal('');
  readonly resumeReference = signal('');
  readonly formatCurrency = formatCurrency;

  ngOnInit(): void {
    const stored = readLastBooking();
    const ref =
      this.route.snapshot.queryParamMap.get('ref') ??
      this.bookingState.booking()?.referenceCode ??
      stored?.outbound.referenceCode ??
      '';
    const returnRef =
      this.route.snapshot.queryParamMap.get('returnRef') ??
      this.bookingState.returnBooking()?.referenceCode ??
      stored?.inbound?.referenceCode ??
      '';

    if (!ref) {
      this.error.set('We could not find this booking. Check your email for the booking reference.');
      return;
    }

    this.loading.set(true);
    forkJoin({
      outbound: this.bookingApi.syncStarpay(ref),
      inbound: returnRef ? this.bookingApi.syncStarpay(returnRef) : of(null),
    }).subscribe({
      next: ({ outbound, inbound }) => {
        this.bookingState.setBooking(outbound.booking);
        this.bookingState.setReturnBooking(inbound?.booking ?? null);
        this.setTickets(outbound.booking, inbound?.booking ?? null);

        const paid = outbound.booking.paymentStatus === 'paid';
        const returnPending =
          !!inbound &&
          inbound.booking.paymentStatus !== 'paid' &&
          inbound.booking.status === 'pending';
        if (paid && returnPending && !this.route.snapshot.queryParamMap.get('leg')) {
          this.continuePayment(outbound.booking.referenceCode, inbound.booking.referenceCode);
          return;
        }

        this.loading.set(false);
        const failed = this.route.snapshot.queryParamMap.get('payment') === 'failed';
        if (!paid && outbound.booking.status === 'pending') {
          this.resumeReference.set(outbound.booking.referenceCode);
          this.resumeUrl.set(outbound.paymentUrl ?? '');
          this.paymentProblem.set(
            failed
              ? 'The payment was not completed. Continue on Starpay to keep these seats.'
              : 'Payment is still open. Continue on Starpay to confirm this booking.',
          );
        } else if (!paid) {
          this.paymentProblem.set(
            'The payment was not completed, so the seats were released. Start checkout again to book.',
          );
        } else if (returnPending) {
          this.resumeReference.set(inbound!.booking.referenceCode);
          this.resumeUrl.set(inbound!.paymentUrl ?? '');
          this.paymentProblem.set('The departing trip is paid. Finish payment for the return trip.');
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set(
          `Your booking ${ref} was created, but we could not confirm the Starpay payment yet.`,
        );
      },
    });
  }

  retryPayment(): void {
    const reference = this.resumeReference();
    if (!reference) return;
    if (this.resumeUrl()) {
      window.location.assign(this.resumeUrl());
      return;
    }
    const outboundRef =
      this.route.snapshot.queryParamMap.get('ref') ??
      this.bookingState.booking()?.referenceCode ??
      reference;
    const returnRef = this.route.snapshot.queryParamMap.get('returnRef') ?? '';
    this.continuePayment(outboundRef, reference === outboundRef ? returnRef : reference);
  }

  private continuePayment(outboundRef: string, payingRef: string): void {
    const origin = window.location.origin;
    const returnQuery = payingRef && payingRef !== outboundRef
      ? `&returnRef=${encodeURIComponent(payingRef)}&leg=return`
      : '';
    const successUrl = `${origin}/booking-confirmed?ref=${encodeURIComponent(outboundRef)}${returnQuery}`;
    const failureUrl = `${origin}/booking-confirmed?ref=${encodeURIComponent(outboundRef)}${returnQuery}&payment=failed`;
    this.bookingApi.startStarpay(payingRef, successUrl, failureUrl).subscribe({
      next: (session) => window.location.assign(session.paymentUrl),
      error: (err) => {
        this.loading.set(false);
        this.paymentProblem.set(starpayError(err));
        this.resumeReference.set(payingRef);
      },
    });
  }

  download(): void {
    window.print();
  }

  private setTickets(outbound: Booking, inbound: Booking | null): void {
    const items = [
      this.toTicket(outbound, inbound ? 'Departing' : 'Trip'),
      ...(inbound ? [this.toTicket(inbound, 'Return')] : []),
    ];
    this.tickets.set(items);
    for (const ticket of items) {
      this.generateQr(ticket.booking.referenceCode);
    }
  }

  private toTicket(booking: Booking, label: string): ConfirmedTicket {
    const method = booking.paymentMethod;
    return {
      booking,
      label,
      routeLabel:
        booking.origin && booking.destination
          ? `${booking.origin} \u2192 ${booking.destination}`
          : `Route #${booking.routeId}`,
      dateLabel: formatDateLong(booking.travelDate),
      departureTime: booking.departureTime ? formatTime12(booking.departureTime) : '',
      terminal: booking.origin ?? '',
      seatLabels: (booking.seats ?? [])
        .map((seat) =>
          seat.seatNumber != null ? `${seat.seatNumber}` : `${seat.row}-${seat.column}`,
        )
        .join(', '),
      paymentLabel: PAYMENT_METHODS.find((m) => m.id === method)?.label ?? method ?? '',
      qrDataUrl: this.fallbackQrUrl(booking.referenceCode),
    };
  }

  private generateQr(reference: string): void {
    import('qrcode')
      .then((mod) => {
        const qr = 'toDataURL' in mod ? mod : (mod as { default: typeof mod }).default;
        return qr.toDataURL(reference, { width: 220, margin: 1 });
      })
      .then((url: string) => this.setQr(reference, url))
      .catch(() => this.setQr(reference, this.fallbackQrUrl(reference)));
  }

  private setQr(reference: string, url: string): void {
    this.tickets.update((tickets) =>
      tickets.map((ticket) =>
        ticket.booking.referenceCode === reference
          ? { ...ticket, qrDataUrl: url }
          : ticket,
      ),
    );
  }

  private fallbackQrUrl(reference: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
      reference,
    )}`;
  }
}
